import {
  BREADBOARD_PIN_KEYS,
  getBreadboardPinInfo,
  isBreadboardComponent,
  stripBreadboardMarkerIndex,
} from '../../utils/projectBuilderBreadboard';
import { get_component_suggestion_pins } from '../../utils/projectBuilderPins';

const POSITIVE_RAIL_ROWS = new Set(['W', 'Y']);
const GROUND_RAIL_ROWS = new Set(['X', 'Z']);

const normalizeIdPart = (value) => String(value || '').trim();

export const buildEndpointId = (partId, pinKey) => (
  `${normalizeIdPart(partId)}:${stripBreadboardMarkerIndex(normalizeIdPart(pinKey))}`
);

export const parseEndpointId = (endpointId) => {
  const safeEndpointId = String(endpointId || '').trim();
  const separatorIndex = safeEndpointId.indexOf(':');

  if (separatorIndex < 0) {
    return {
      partId: '',
      pinKey: stripBreadboardMarkerIndex(safeEndpointId),
    };
  }

  return {
    partId: safeEndpointId.slice(0, separatorIndex),
    pinKey: stripBreadboardMarkerIndex(safeEndpointId.slice(separatorIndex + 1)),
  };
};

const normalizeHoleId = (holeIdOrDescriptor) => {
  if (typeof holeIdOrDescriptor === 'string') {
    const { partId, pinKey } = parseEndpointId(holeIdOrDescriptor);
    return buildEndpointId(partId, pinKey);
  }

  return buildEndpointId(
    holeIdOrDescriptor?.breadboardId || holeIdOrDescriptor?.partId,
    holeIdOrDescriptor?.pinKey
  );
};

class UnionFind {
  constructor() {
    this.parent = new Map();
  }

  add(value) {
    const key = String(value || '').trim();

    if (!key) {
      return '';
    }

    if (!this.parent.has(key)) {
      this.parent.set(key, key);
    }

    return key;
  }

  find(value) {
    const key = this.add(value);

    if (!key) {
      return '';
    }

    const parent = this.parent.get(key);

    if (parent === key) {
      return key;
    }

    const root = this.find(parent);
    this.parent.set(key, root);
    return root;
  }

  union(left, right) {
    const leftRoot = this.find(left);
    const rightRoot = this.find(right);

    if (!leftRoot || !rightRoot || leftRoot === rightRoot) {
      return;
    }

    this.parent.set(rightRoot, leftRoot);
  }

  values() {
    return Array.from(this.parent.keys());
  }
}

export const getBreadboardRailRole = (pinKey) => {
  const info = getBreadboardPinInfo(pinKey);

  if (!info || info.rowGroup !== 'rail') {
    return '';
  }

  if (POSITIVE_RAIL_ROWS.has(info.row)) {
    return 'positive';
  }

  if (GROUND_RAIL_ROWS.has(info.row)) {
    return 'ground';
  }

  return '';
};

export const isBreadboardTerminalHole = (pinKey) => {
  const info = getBreadboardPinInfo(pinKey);
  return info?.rowGroup === 'terminal-lower' || info?.rowGroup === 'terminal-upper';
};

export const isBreadboardPositiveRailHole = (pinKey) => (
  getBreadboardRailRole(pinKey) === 'positive'
);

export const isBreadboardGroundRailHole = (pinKey) => (
  getBreadboardRailRole(pinKey) === 'ground'
);

const shouldSplitRails = (breadboardComponent, options = {}) => (
  Boolean(options.splitRails) ||
  Boolean(breadboardComponent?.splitPowerRails) ||
  Boolean(breadboardComponent?.breadboardOptions?.splitPowerRails)
);

const getRailSplitSegment = (column, breadboardComponent, options = {}) => {
  const splitAt = Number(
    options.splitRailAtColumn ||
    breadboardComponent?.railSplitAtColumn ||
    breadboardComponent?.breadboardOptions?.railSplitAtColumn ||
    32
  );

  if (!shouldSplitRails(breadboardComponent, options)) {
    return 'all';
  }

  return Number(column) < splitAt ? 'left' : 'right';
};

export const getInternalGroupForHole = (
  holeIdOrDescriptor,
  projectState = {},
  options = {}
) => {
  const holeId = normalizeHoleId(holeIdOrDescriptor);
  const { partId, pinKey } = parseEndpointId(holeId);
  const info = getBreadboardPinInfo(pinKey);

  if (!partId || !info) {
    return '';
  }

  if (info.rowGroup === 'rail') {
    const breadboardComponent = (projectState.components || []).find(
      (component) => String(component?.id) === String(partId)
    );
    const segment = getRailSplitSegment(info.column, breadboardComponent, options);
    return `${partId}:rail:${info.row}:${segment}`;
  }

  return `${partId}:${info.busKey}`;
};

const addEndpointPins = (uf, components = []) => {
  components.forEach((component) => {
    get_component_suggestion_pins(component).forEach((pin) => {
      uf.add(buildEndpointId(pin.component_id, pin.marker_key));
    });
  });
};

const addBreadboardInternalEdges = (uf, components = [], options = {}) => {
  components
    .filter(isBreadboardComponent)
    .forEach((component) => {
      const groups = new Map();

      BREADBOARD_PIN_KEYS.forEach((pinKey) => {
        const endpointId = buildEndpointId(component.id, pinKey);
        const groupId = getInternalGroupForHole(endpointId, { components }, options);

        if (!groupId) {
          return;
        }

        if (!groups.has(groupId)) {
          groups.set(groupId, []);
        }

        groups.get(groupId).push(endpointId);
        uf.add(endpointId);
      });

      groups.forEach((endpointIds) => {
        const [firstEndpointId, ...restEndpointIds] = endpointIds;

        restEndpointIds.forEach((endpointId) => {
          uf.union(firstEndpointId, endpointId);
        });
      });
    });
};

const addConnectionEdges = (uf, connections = []) => {
  connections.forEach((connection) => {
    const fromId = buildEndpointId(connection?.from?.partId, connection?.from?.pinKey);
    const toId = buildEndpointId(connection?.to?.partId, connection?.to?.pinKey);

    if (!fromId || !toId || fromId === ':' || toId === ':') {
      return;
    }

    uf.union(fromId, toId);
  });
};

const isPowerSourceComponent = (component) => {
  const passiveType = String(component?.passiveType || component?.templateType || '').trim();
  return (
    passiveType === 'dc_power' ||
    passiveType === 'voltage_source' ||
    Boolean(component?.powerSource)
  );
};

const getPowerSourceTerminalRoles = (component) => {
  if (!isPowerSourceComponent(component)) {
    return [];
  }

  return [
    { pinKey: '+', role: 'positive' },
    { pinKey: 'V+', role: 'positive' },
    { pinKey: 'POS', role: 'positive' },
    { pinKey: '-', role: 'ground' },
    { pinKey: 'GND', role: 'ground' },
    { pinKey: 'NEG', role: 'ground' },
  ];
};

const getNetPowerRoleSets = (components, endpointNetId) => {
  const rolesByNetId = new Map();

  const addRole = (endpointId, role) => {
    const netId = endpointNetId.get(endpointId);

    if (!netId || !role) {
      return;
    }

    if (!rolesByNetId.has(netId)) {
      rolesByNetId.set(netId, new Set());
    }

    rolesByNetId.get(netId).add(role);
  };

  components.forEach((component) => {
    getPowerSourceTerminalRoles(component).forEach(({ pinKey, role }) => {
      addRole(buildEndpointId(component.id, pinKey), role);
    });
  });

  return rolesByNetId;
};

const resolvePowerState = (roleSet) => {
  const hasPositive = roleSet?.has('positive');
  const hasGround = roleSet?.has('ground');

  if (hasPositive && hasGround) {
    return 'conflict';
  }

  if (hasPositive) {
    return 'positive';
  }

  if (hasGround) {
    return 'ground';
  }

  return 'unpowered';
};

export const buildBreadboardConnectivityModel = (projectState = {}, options = {}) => {
  const components = Array.isArray(projectState.components) ? projectState.components : [];
  const connections = Array.isArray(projectState.connections) ? projectState.connections : [];
  const uf = new UnionFind();

  addEndpointPins(uf, components);
  addBreadboardInternalEdges(uf, components, options);
  addConnectionEdges(uf, connections);

  const endpointNetId = new Map();
  const endpointIdsByRoot = new Map();

  uf.values().forEach((endpointId) => {
    const root = uf.find(endpointId);

    if (!endpointIdsByRoot.has(root)) {
      endpointIdsByRoot.set(root, []);
    }

    endpointIdsByRoot.get(root).push(endpointId);
  });

  Array.from(endpointIdsByRoot.entries()).forEach(([, endpointIds], index) => {
    const netId = `net-${index + 1}`;

    endpointIds.forEach((endpointId) => {
      endpointNetId.set(endpointId, netId);
    });
  });

  const powerRolesByNetId = getNetPowerRoleSets(components, endpointNetId);
  const breadboardComponentIds = new Set(
    components.filter(isBreadboardComponent).map((component) => String(component.id))
  );
  const nets = Array.from(endpointIdsByRoot.values()).map((endpointIds, index) => {
    const netId = `net-${index + 1}`;
    const holeIds = endpointIds.filter((endpointId) => {
      const { partId, pinKey } = parseEndpointId(endpointId);
      return breadboardComponentIds.has(String(partId)) && Boolean(getBreadboardPinInfo(pinKey));
    });

    return {
      id: netId,
      endpointIds,
      holeIds,
      powerState: resolvePowerState(powerRolesByNetId.get(netId)),
    };
  });

  return {
    endpointNetId,
    nets,
    getEndpointNetId(endpointId) {
      return endpointNetId.get(normalizeHoleId(endpointId)) || '';
    },
    getNetById(netId) {
      return nets.find((net) => net.id === netId) || null;
    },
    getNetForEndpoint(endpointId) {
      return this.getNetById(this.getEndpointNetId(endpointId));
    },
  };
};

export const getConnectedNetForHole = (holeId, projectState = {}, options = {}) => {
  const model = buildBreadboardConnectivityModel(projectState, options);
  return model.getNetForEndpoint(normalizeHoleId(holeId));
};

export const areHolesElectricallyConnected = (
  holeA,
  holeB,
  projectState = {},
  options = {}
) => {
  const model = buildBreadboardConnectivityModel(projectState, options);
  const netA = model.getEndpointNetId(normalizeHoleId(holeA));
  const netB = model.getEndpointNetId(normalizeHoleId(holeB));

  return Boolean(netA && netB && netA === netB);
};

export const getConnectedHoleIds = (holeId, projectState = {}, options = {}) => {
  const net = getConnectedNetForHole(holeId, projectState, options);
  return net?.holeIds || [];
};

export const getNetPowerStateForEndpoint = (
  endpointId,
  projectState = {},
  options = {}
) => {
  const net = buildBreadboardConnectivityModel(projectState, options)
    .getNetForEndpoint(endpointId);

  return net?.powerState || 'unpowered';
};

export const getPowerStateForHole = (holeId, projectState = {}, options = {}) => (
  getNetPowerStateForEndpoint(normalizeHoleId(holeId), projectState, options)
);

export const getProjectPowerConflicts = (projectState = {}, options = {}) => (
  buildBreadboardConnectivityModel(projectState, options)
    .nets
    .filter((net) => net.powerState === 'conflict')
);

export const findBreadboardComponents = (projectState = {}) => (
  (Array.isArray(projectState.components) ? projectState.components : [])
    .filter(isBreadboardComponent)
);

export const getPrimaryBreadboardId = (projectState = {}) => (
  findBreadboardComponents(projectState)[0]?.id || ''
);
